import React, { useState, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NetworkErrorAlert from '../components/NetworkErrorAlert';
import GoogleSignInButton from '../components/GoogleSignInButton';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

// Pricing tiers for plan selection
const pricingTiers = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    currency: 'GBP',
    appointments: 15,
    description: 'Perfect for getting started',
    features: [
      '15 appointments per month',
      'Basic booking widget',
      'Email confirmations',
      'Calendiary branding on widget',
    ],
  },
  {
    id: 'starter',
    name: 'Starter',
    price: 27,
    currency: 'GBP',
    appointments: 100,
    description: 'For growing practices',
    features: [
      '100 appointments per month',
      'Customizable booking widget',
      'Remove Calendiary branding',
      'Priority support',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 57,
    currency: 'GBP',
    appointments: 500,
    description: 'For busy practitioners',
    features: [
      '500 appointments per month',
      'Full widget customization',
      'Advanced analytics',
    ],
    popular: true,
  },
  {
    id: 'business',
    name: 'Business',
    price: 97,
    currency: 'GBP',
    appointments: 1000,
    description: 'For clinics and teams',
    features: [
      '1000 appointments per month',
      'Multi-doctor support',
      'Dedicated account manager',
    ],
  },
];

const CheckIcon = () => (
  <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const Register = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();

  // Multi-step form state
  const [currentStep, setCurrentStep] = useState(1);
  const preSelectedPlan = searchParams.get('plan');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'patient', // patient or doctor
    selectedPlan: preSelectedPlan || 'free',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  // Determine total steps based on role
  const getTotalSteps = () => {
    if (formData.role === 'doctor' || formData.role === 'clinic_owner') {
      return 3; // 1: Info, 2: Plan Selection, 3: Payment/Confirmation
    }
    return 1; // Patients have single step
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    setSubmitError('');
  };

  const handlePlanSelect = (planId) => {
    setFormData((prev) => ({
      ...prev,
      selectedPlan: planId,
    }));
  };

  const validateStep1 = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (currentStep === 1 && !validateStep1()) {
      return;
    }
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const performRegistration = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitError('');
    setIsNetworkError(false);

    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        role: formData.role
      });

      // If doctor/clinic with paid plan, upgrade subscription
      if ((formData.role === 'doctor' || formData.role === 'clinic_owner') && formData.selectedPlan !== 'free') {
        try {
          const token = localStorage.getItem('token');
          await fetch(`${API_URL}/subscriptions/upgrade`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ planTier: formData.selectedPlan })
          });
        } catch (err) {
          console.error('Failed to upgrade subscription:', err);
          // Continue anyway - user can upgrade later
        }
      }

      setSubmitSuccess(true);

      // Redirect based on role
      setTimeout(() => {
        if (formData.role === 'doctor') {
          navigate('/doctor/dashboard');
        } else if (formData.role === 'clinic_owner') {
          navigate('/clinic/dashboard');
        } else {
          navigate('/patient/dashboard');
        }
      }, 1500);
    } catch (error) {
      console.error('Registration error:', error);

      // Check if this is a network error
      if (error.isNetworkError || error.name === 'NetworkError') {
        setIsNetworkError(true);
        setSubmitError(error.message);
      } else {
        setIsNetworkError(false);
        setSubmitError(error.message || 'Registration failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
      setIsRetrying(false);
    }
  }, [formData, navigate, register]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);
    setIsNetworkError(false);

    // For patients, validate and submit directly
    if (formData.role === 'patient') {
      if (!validateStep1()) return;
      await performRegistration();
      return;
    }

    // For doctors/clinics on final step, submit
    if (currentStep === getTotalSteps()) {
      await performRegistration();
    }
  };

  const handleRetry = useCallback(async () => {
    setIsRetrying(true);
    await performRegistration();
  }, [performRegistration]);

  // Handle Google sign-in success
  const handleGoogleSuccess = useCallback((data) => {
    // Store token
    localStorage.setItem('token', data.token);
    localStorage.setItem('rememberMe', 'true');

    setSubmitSuccess(true);

    // Redirect based on role
    setTimeout(() => {
      if (data.user.role === 'doctor') {
        navigate('/doctor/dashboard');
      } else if (data.user.role === 'clinic_owner') {
        navigate('/clinic/dashboard');
      } else {
        navigate('/patient/dashboard');
      }
    }, 1500);
  }, [navigate]);

  // Handle Google sign-in error
  const handleGoogleError = useCallback((message) => {
    setSubmitError(message);
    setIsNetworkError(false);
  }, []);

  const selectedTier = pricingTiers.find((t) => t.id === formData.selectedPlan);

  // Render step indicators for multi-step
  const renderStepIndicator = () => {
    const totalSteps = getTotalSteps();
    if (totalSteps === 1) return null;

    return (
      <div className="flex items-center justify-center gap-2 mb-8">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs ${
                currentStep >= step
                  ? 'bg-silver-gradient text-obsidian'
                  : 'border border-light-border dark:border-border text-slate-400 dark:text-silver/40'
              }`}
            >
              {step}
            </div>
            {step < 3 && (
              <div
                className={`w-12 h-0.5 ${
                  currentStep > step ? 'bg-slate-500 dark:bg-silver' : 'bg-light-border dark:bg-border'
                }`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  // Step 1: Basic Info
  const renderStep1 = () => (
    <>
      {/* Role Selection */}
      <div>
        <label className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-3">
          I am a
        </label>
        <div className="flex gap-3">
          <label
            className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border cursor-pointer transition-smooth ${
              formData.role === 'patient'
                ? 'border-light-text dark:border-pure-white bg-slate-100 dark:bg-white/5'
                : 'border-light-border dark:border-border hover:border-slate-400 dark:hover:border-white/20'
            }`}
          >
            <input
              type="radio"
              name="role"
              value="patient"
              checked={formData.role === 'patient'}
              onChange={handleChange}
              className="sr-only"
            />
            <span className="font-mono text-xs uppercase tracking-wider">
              Patient
            </span>
          </label>
          <label
            className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border cursor-pointer transition-smooth ${
              formData.role === 'doctor'
                ? 'border-light-text dark:border-pure-white bg-slate-100 dark:bg-white/5'
                : 'border-light-border dark:border-border hover:border-slate-400 dark:hover:border-white/20'
            }`}
          >
            <input
              type="radio"
              name="role"
              value="doctor"
              checked={formData.role === 'doctor'}
              onChange={handleChange}
              className="sr-only"
            />
            <span className="font-mono text-xs uppercase tracking-wider">
              Doctor
            </span>
          </label>
          <label
            className={`flex-1 flex items-center justify-center gap-2 p-4 rounded-xl border cursor-pointer transition-smooth ${
              formData.role === 'clinic_owner'
                ? 'border-light-text dark:border-pure-white bg-slate-100 dark:bg-white/5'
                : 'border-light-border dark:border-border hover:border-slate-400 dark:hover:border-white/20'
            }`}
          >
            <input
              type="radio"
              name="role"
              value="clinic_owner"
              checked={formData.role === 'clinic_owner'}
              onChange={handleChange}
              className="sr-only"
            />
            <span className="font-mono text-xs uppercase tracking-wider">
              Clinic
            </span>
          </label>
        </div>
      </div>

      {/* Google Sign Up */}
      <div className="space-y-4">
        <GoogleSignInButton
          role={formData.role}
          onSuccess={handleGoogleSuccess}
          onError={handleGoogleError}
          disabled={isSubmitting}
        />

        {/* Divider */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-light-border dark:bg-border"></div>
          <span className="text-xs text-light-text-secondary dark:text-silver/60 font-mono uppercase tracking-widest">
            or register with email
          </span>
          <div className="flex-1 h-px bg-light-border dark:bg-border"></div>
        </div>
      </div>

      {/* Name Fields */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="firstName"
            className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2"
          >
            First Name
          </label>
          <input
            type="text"
            id="firstName"
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            className={`input-field ${errors.firstName ? 'border-red-500' : ''}`}
            placeholder="John"
            disabled={isSubmitting}
            aria-invalid={errors.firstName ? 'true' : 'false'}
            aria-describedby={errors.firstName ? 'firstName-error' : undefined}
          />
          {errors.firstName && (
            <p id="firstName-error" className="mt-1 text-xs text-red-500" role="alert" aria-live="assertive">{errors.firstName}</p>
          )}
        </div>
        <div>
          <label
            htmlFor="lastName"
            className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2"
          >
            Last Name
          </label>
          <input
            type="text"
            id="lastName"
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            className={`input-field ${errors.lastName ? 'border-red-500' : ''}`}
            placeholder="Doe"
            disabled={isSubmitting}
            aria-invalid={errors.lastName ? 'true' : 'false'}
            aria-describedby={errors.lastName ? 'lastName-error' : undefined}
          />
          {errors.lastName && (
            <p id="lastName-error" className="mt-1 text-xs text-red-500" role="alert" aria-live="assertive">{errors.lastName}</p>
          )}
        </div>
      </div>

      {/* Email */}
      <div>
        <label
          htmlFor="email"
          className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2"
        >
          Email Address
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`input-field ${errors.email ? 'border-red-500' : ''}`}
          placeholder="john.doe@example.com"
          disabled={isSubmitting}
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby={errors.email ? 'email-error' : undefined}
        />
        {errors.email && (
          <p id="email-error" className="mt-1 text-xs text-red-500" role="alert" aria-live="assertive">{errors.email}</p>
        )}
      </div>

      {/* Password */}
      <div>
        <label
          htmlFor="password"
          className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2"
        >
          Password
        </label>
        <input
          type="password"
          id="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          className={`input-field ${errors.password ? 'border-red-500' : ''}`}
          placeholder="••••••••"
          disabled={isSubmitting}
          aria-invalid={errors.password ? 'true' : 'false'}
          aria-describedby={errors.password ? 'password-error' : undefined}
        />
        {errors.password && (
          <p id="password-error" className="mt-1 text-xs text-red-500" role="alert" aria-live="assertive">{errors.password}</p>
        )}
      </div>

      {/* Confirm Password */}
      <div>
        <label
          htmlFor="confirmPassword"
          className="block font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2"
        >
          Confirm Password
        </label>
        <input
          type="password"
          id="confirmPassword"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          className={`input-field ${errors.confirmPassword ? 'border-red-500' : ''}`}
          placeholder="••••••••"
          disabled={isSubmitting}
          aria-invalid={errors.confirmPassword ? 'true' : 'false'}
          aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
        />
        {errors.confirmPassword && (
          <p id="confirmPassword-error" className="mt-1 text-xs text-red-500" role="alert" aria-live="assertive">{errors.confirmPassword}</p>
        )}
      </div>
    </>
  );

  // Step 2: Plan Selection
  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="font-display text-2xl italic mb-2">Choose Your Plan</h2>
        <p className="text-light-text-secondary dark:text-silver/60 text-sm">
          Select the plan that fits your practice
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {pricingTiers.map((tier) => (
          <button
            key={tier.id}
            type="button"
            onClick={() => handlePlanSelect(tier.id)}
            data-plan={tier.id}
            className={`relative p-4 rounded-xl border text-left transition-smooth ${
              formData.selectedPlan === tier.id
                ? 'border-light-text dark:border-pure-white bg-slate-100 dark:bg-white/10 ring-2 ring-slate-300 dark:ring-silver/30'
                : 'border-light-border dark:border-border hover:border-slate-400 dark:hover:border-white/30'
            }`}
          >
            {tier.popular && (
              <span className="absolute -top-2 left-4 bg-silver-gradient text-obsidian font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full">
                Popular
              </span>
            )}

            {formData.selectedPlan === tier.id && (
              <div className="absolute top-3 right-3">
                <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <CheckIcon />
                </div>
              </div>
            )}

            <div className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-1">
              {tier.name}
            </div>
            <div className="flex items-baseline gap-1 mb-2">
              <span className="font-display text-2xl italic">
                {tier.price === 0 ? 'Free' : `£${tier.price}`}
              </span>
              {tier.price > 0 && (
                <span className="font-mono text-xs text-light-text-secondary dark:text-silver/60">/month</span>
              )}
            </div>
            <div className="text-xs text-light-text-secondary dark:text-silver/60 mb-3">
              {tier.appointments} appointments/month
            </div>
            <ul className="space-y-1">
              {tier.features.slice(0, 3).map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2 text-xs text-light-text-secondary dark:text-silver/60">
                  <CheckIcon />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </button>
        ))}
      </div>
    </div>
  );

  // Step 3: Payment/Confirmation
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <h2 className="font-display text-2xl italic mb-2">Complete Registration</h2>
        <p className="text-light-text-secondary dark:text-silver/60 text-sm">
          Review your selection and complete your account
        </p>
      </div>

      {/* Order Summary */}
      <div className="glass border border-light-border dark:border-border rounded-xl p-6">
        <h3 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
          Order Summary
        </h3>

        <div className="flex justify-between items-center pb-4 border-b border-light-border dark:border-border">
          <div>
            <div className="font-display text-lg italic">{selectedTier?.name} Plan</div>
            <div className="text-xs text-light-text-secondary dark:text-silver/60">
              {selectedTier?.appointments} appointments per month
            </div>
          </div>
          <div className="text-right">
            <div className="font-display text-xl italic" data-testid="plan-amount">
              {selectedTier?.price === 0 ? 'Free' : `£${selectedTier?.price}`}
            </div>
            {selectedTier?.price > 0 && (
              <div className="text-xs text-light-text-secondary dark:text-silver/60">/month</div>
            )}
          </div>
        </div>

        <div className="pt-4 flex justify-between items-center">
          <span className="font-mono text-sm uppercase tracking-wider">Total Due Today</span>
          <span className="font-display text-2xl italic text-light-text dark:text-pure-white" data-testid="total-amount">
            {selectedTier?.price === 0 ? 'Free' : `£${selectedTier?.price}`}
          </span>
        </div>
      </div>

      {/* Account Info Summary */}
      <div className="glass border border-light-border dark:border-border rounded-xl p-6">
        <h3 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
          Account Details
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-light-text-secondary dark:text-silver/60">Name</span>
            <span>{formData.firstName} {formData.lastName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-light-text-secondary dark:text-silver/60">Email</span>
            <span>{formData.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-light-text-secondary dark:text-silver/60">Account Type</span>
            <span className="capitalize">{formData.role.replace('_', ' ')}</span>
          </div>
        </div>
      </div>

      {selectedTier?.price > 0 && (
        <div className="text-center text-xs text-light-text-secondary dark:text-silver/60">
          <p>By completing registration, you agree to our Terms of Service.</p>
          <p className="mt-1">Payment processing is simulated in development mode.</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-5rem)] px-4 py-8">
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
            {currentStep === 1 && 'Create Account'}
            {currentStep === 2 && 'Choose Plan'}
            {currentStep === 3 && 'Complete Setup'}
          </h1>
          <p className="font-body text-light-text-secondary dark:text-silver/60">
            {currentStep === 1 && 'Join Calendiary to start booking appointments'}
            {currentStep === 2 && 'Select the plan that fits your practice'}
            {currentStep === 3 && 'Review and confirm your subscription'}
          </p>
        </div>

        {/* Step Indicator */}
        {(formData.role === 'doctor' || formData.role === 'clinic_owner') && renderStepIndicator()}

        {/* Registration Form */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-8">
          {submitSuccess && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl text-green-400 text-sm">
              Registration successful! Redirecting...
            </div>
          )}

          {isNetworkError && submitError && (
            <NetworkErrorAlert
              onRetry={handleRetry}
              isRetrying={isRetrying}
              message={submitError}
            />
          )}

          {!isNetworkError && submitError && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm" role="alert" aria-live="assertive">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}

            {/* Navigation Buttons */}
            <div className="flex gap-4">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="flex-1 py-3 px-6 rounded-lg font-mono text-xs uppercase tracking-widest border border-light-border dark:border-border text-light-text-secondary dark:text-silver hover:bg-slate-100 dark:hover:bg-white/5 transition-smooth"
                >
                  Back
                </button>
              )}

              {/* For patients or last step: Submit button */}
              {(formData.role === 'patient' || currentStep === getTotalSteps()) ? (
                <button
                  type="submit"
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? 'Creating Account...'
                    : selectedTier?.price > 0
                      ? `Pay £${selectedTier.price} & Create Account`
                      : 'Create Account'
                  }
                </button>
              ) : (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleNextStep();
                  }}
                  className="flex-1 btn-primary"
                >
                  Continue
                </button>
              )}
            </div>
          </form>

          {/* Login Link */}
          <p className="mt-6 text-center text-light-text-secondary dark:text-silver/60 font-body text-sm">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-light-text dark:text-pure-white hover:underline transition-smooth"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
